import React from "react";
import {
  Table,
  Select,
  DatePicker,
  Space,
  Tag,
  Typography,
  Empty,
  Alert,
  Button,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RangePickerProps } from "antd/es/date-picker";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useGetAuditLogsQuery } from "../../../store/api/auditApi";
import {
  AuditAction,
  AuditLogEntry,
} from "../../../services/audit/audit.types";
import {
  KNOWN_AUDIT_ACTIONS,
  resolveAuditActionTag,
} from "../../../constants/status-colors";
import { getPayloadSections, getEntityLabel, hasContent } from "./utils";
import PageHeader from "../../../components/PageHeader";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Built from the shared tag map so the filter offers exactly the actions the
// table can name — including the service-level verbs (READ_CREDENTIALS,
// BULK_COMMIT, ...), which the backend now accepts as a filter value too.
const ACTION_OPTIONS: { value: AuditAction; label: string }[] =
  KNOWN_AUDIT_ACTIONS.map((action) => ({
    value: action,
    label: resolveAuditActionTag(action).label,
  }));

// `entity_type` stores the first segment of the backend route (see
// audit-log.interceptor.ts), so these values must match route prefixes, not
// display names. Curated to the entities operators actually mutate.
const ENTITY_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "programs", label: "Programas (programs)" },
  { value: "courses", label: "Cursos (courses)" },
  { value: "lessons", label: "Lecciones (lessons)" },
  { value: "resources", label: "Recursos (resources)" },
  { value: "game-quizzes", label: "Quizzes Emmi (game-quizzes)" },
  { value: "projects", label: "Proyectos (projects)" },
  { value: "reading-content", label: "Lecturas (reading-content)" },
  { value: "slides", label: "Slides (slides)" },
  { value: "schools", label: "Escuelas (schools)" },
  { value: "users", label: "Usuarios (users)" },
  { value: "classrooms", label: "Salones (classrooms)" },
  { value: "subjects", label: "Asignaturas (subjects)" },
  { value: "grades", label: "Grados (grades)" },
  { value: "calendar", label: "Calendario (calendar)" },
  { value: "events", label: "Eventos (events)" },
  { value: "tienda", label: "Tienda (tienda)" },
  { value: "content-ingestion", label: "Subidor de contenido (content-ingestion)" },
  { value: "admin", label: "Papelera y herramientas (admin)" },
  { value: "app-version", label: "Versión de la app (app-version)" },
];

const DEFAULT_PAGE_SIZE = 20;

// A deep link may only select an action the UI can name; anything else is a
// stale or hand-edited URL and is ignored rather than sent to the backend.
const parseAction = (value: string | null): AuditAction | undefined =>
  KNOWN_AUDIT_ACTIONS.find((action) => action === value);

/** ISO datetimes only; anything unparseable is ignored (bad deep link). */
const parseIsoDate = (value: string | null): string | undefined =>
  value && dayjs(value).isValid() ? value : undefined;

const Activity: React.FC = () => {
  // The URL is the source of truth for every filter plus page/pageSize, so
  // any filtered view is deep-linkable and survives refreshes.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const action = parseAction(searchParams.get("action"));
  const entityType = searchParams.get("entity_type") ?? undefined;
  const entityIdParam = searchParams.get("entity_id") ?? "";
  const entityId = /^\d+$/.test(entityIdParam)
    ? parseInt(entityIdParam, 10)
    : undefined;
  const from = parseIsoDate(searchParams.get("from"));
  const to = parseIsoDate(searchParams.get("to"));

  // RTK Query keys the cache by the whole arg, which also kills the race the
  // old useEffect version had: a stale response can't overwrite a newer one.
  const { data, isFetching, isError, refetch } = useGetAuditLogsQuery({
    page,
    limit: pageSize,
    ...(action && { action }),
    ...(entityType && { entity_type: entityType }),
    ...(entityId !== undefined && { entity_id: entityId }),
    ...(from && { from }),
    ...(to && { to }),
  });

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;

  // Filter changes always reset to page 1 (dropping the stale page param).
  const setFilterParams = (entries: Record<string, string | undefined>) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(entries).forEach(([key, value]) => {
          if (value) next.set(key, value);
          else next.delete(key);
        });
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const handleActionChange = (value: AuditAction | undefined) => {
    setFilterParams({ action: value });
  };

  const handleEntityTypeChange = (value: string | undefined) => {
    setFilterParams({ entity_type: value });
  };

  const handleDateRangeChange: NonNullable<RangePickerProps["onChange"]> = (
    dates
  ) => {
    // Full ISO datetimes with explicit day boundaries: a bare "YYYY-MM-DD"
    // is parsed by the backend as UTC midnight, which in UTC-negative
    // timezones silently excludes almost the entire selected end day.
    setFilterParams({
      from: dates?.[0] ? dates[0].startOf("day").toISOString() : undefined,
      to: dates?.[1] ? dates[1].endOf("day").toISOString() : undefined,
    });
  };

  const handleTableChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  const dateRange: RangePickerProps["value"] =
    from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;

  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: "Fecha",
      dataIndex: "created_at",
      key: "created_at",
      render: (createdAt: string) => dayjs(createdAt).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Usuario",
      key: "actor",
      render: (_: unknown, record: AuditLogEntry) => (
        <Space direction="vertical" size={0}>
          <Text>{record.actor_name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.actor_email}
          </Text>
        </Space>
      ),
    },
    {
      title: "Acción",
      dataIndex: "action",
      key: "action",
      render: (actionValue: AuditAction) => {
        const tag = resolveAuditActionTag(actionValue);
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Entidad",
      key: "entity",
      render: (_: unknown, record: AuditLogEntry) => {
        if (!record.entity_type) return "—";
        const reference = `${record.entity_type}${
          record.entity_id !== null ? ` #${record.entity_id}` : ""
        }`;
        const label = getEntityLabel(record.payload);
        return (
          <Space direction="vertical" size={0}>
            <Text>{reference}</Text>
            {label && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {label}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Ruta",
      dataIndex: "path",
      key: "path",
      ellipsis: true,
      render: (path: string) => <Text code>{path}</Text>,
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader title="Actividad" />
      <Space wrap>
        <Select<AuditAction | undefined>
          allowClear
          placeholder="Acción"
          style={{ width: 160 }}
          value={action}
          onChange={handleActionChange}
          options={ACTION_OPTIONS}
        />
        <Select<string | undefined>
          allowClear
          showSearch
          placeholder="Tipo de entidad"
          style={{ width: 260 }}
          value={entityType}
          onChange={handleEntityTypeChange}
          options={ENTITY_TYPE_OPTIONS}
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
        <RangePicker value={dateRange} onChange={handleDateRangeChange} />
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          message="Error al cargar el historial de actividad."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}

      <Table<AuditLogEntry>
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={isFetching}
        locale={{
          emptyText: (
            <Empty description="Sin actividad registrada para los filtros elegidos." />
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: handleTableChange,
        }}
        expandable={{
          expandedRowRender: (record) => {
            const { request, response } = getPayloadSections(record.payload);
            if (!hasContent(request) && !hasContent(response)) {
              return <Text type="secondary">Sin datos adicionales.</Text>;
            }
            return (
              <Space direction="vertical" style={{ width: "100%" }} size="small">
                {hasContent(request) && (
                  <div>
                    <Text strong>Datos enviados</Text>
                    <pre style={{ maxHeight: 300, overflow: "auto", margin: 0 }}>
                      {JSON.stringify(request, null, 2)}
                    </pre>
                  </div>
                )}
                {hasContent(response) && (
                  <div>
                    <Text strong>Resultado</Text>
                    <pre style={{ maxHeight: 300, overflow: "auto", margin: 0 }}>
                      {JSON.stringify(response, null, 2)}
                    </pre>
                  </div>
                )}
              </Space>
            );
          },
        }}
      />
    </Space>
  );
};

export default Activity;
